/**
 * Lululemon-style Accordion Component
 * Features:
 * - Auto-close other accordions when opening (mobile)
 * - Smooth height animation
 * - Animated +/- icon
 * - Keyboard accessible
 */

class LuluAccordion extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  constructor() {
    super();
    this.details = null;
    this.summary = null;
    this.content = null;
    this.icon = null;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.lulu-accordion__content');
    this.icon = this.querySelector('.lulu-accordion__icon');

    if (!this.details || !this.summary) return;

    // Prevent default toggle, we'll handle it ourselves
    this.summary.addEventListener('click', (e) => this.onClick(e));

    // Handle keyboard
    this.summary.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  onClick(e) {
    e.preventDefault();

    // Don't allow toggling during animation
    if (this.isClosing || this.isExpanding) return;

    this.toggle();
  }

  toggle() {
    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Auto-close siblings on mobile only (prevents wall of text)
    // Desktop allows multiple open (more screen space)
    if (window.innerWidth < 750) {
      this.closeSiblings();
    }

    this.details.style.overflow = 'hidden';
    this.details.open = true;
    this.isExpanding = true;

    // Animate from 0 to full height
    const startHeight = `${this.summary.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

    if (this.animation) this.animation.cancel();

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 200, easing: 'ease-out' }
    );

    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  close() {
    this.isClosing = true;
    this.details.style.overflow = 'hidden';

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) this.animation.cancel();

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 150, easing: 'ease-out' }
    );

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
  }

  closeSiblings() {
    // Find parent accordion group
    const parent = this.closest('.lulu-accordion-group, .product-details, [data-accordion-group]');
    if (!parent) return;

    // Close all other accordions in the group
    const siblings = parent.querySelectorAll('lulu-accordion');
    siblings.forEach((accordion) => {
      if (accordion !== this && accordion.details?.open) {
        accordion.close();
      }
    });
  }
}

// Register the custom element
if (!customElements.get('lulu-accordion')) {
  customElements.define('lulu-accordion', LuluAccordion);
}
